import Papa from 'papaparse';
import { deriveImportStatus, resolveInventoryId } from '@ncc/shared';

export type ImportRow = {
  status: 'draft' | 'published' | 'hidden' | 'sold';
  inventory_id: string;
  year: number;
  regular_price: number | null;
  sale_price: number | null;
  brand: string | null;
  inventory_type: string | null;
  tag: string | null;
  vehicle_name: string;
  model_number: string | null;
  mileage: number | null;
  color: string | null;
  condition: 'New' | 'Used';
  youtube_link: string | null;
  description: string;
  gawr: number | null;
  gross_capacity: number | null;
  curb_weight: number | null;
  payload_capacity: number | null;
  tie_downs: string | null;
  lights: string | null;
  hitch: string | null;
  jack: string | null;
  axles: string | null;
  suspensions: string | null;
  tires_wheels: string | null;
  rim_size: string | null;
  trailer_plug: string | null;
  ramp: string | null;
  frame_construction: string | null;
  deck_construction: string | null;
  overall_length: string | null;
  overall_width: string | null;
  overall_height: string | null;
  interior_length: string | null;
  interior_width: string | null;
  interior_height: string | null;
  deck_length: string | null;
  deck_width: string | null;
  deck_height: string | null;
  enclosed_door_size: string | null;
  date_uploaded: string | null;
  images: string[];
};

export type ImportResult = { rows: ImportRow[]; errors: string[] };

function parseMoney(s: string | null | undefined): number | null {
  if (s == null) return null;
  const trimmed = s.trim();
  if (!trimmed || trimmed.toUpperCase() === 'NA') return null;
  const cleaned = trimmed.replace(/[$,]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

function parseInt32(s: string | null | undefined): number | null {
  if (s == null) return null;
  const trimmed = s.trim();
  if (!trimmed) return null;
  const cleaned = trimmed.replace(/[^0-9-]/g, '');
  if (!cleaned) return null;
  const n = parseInt(cleaned, 10);
  return Number.isFinite(n) ? n : null;
}

function emptyToNull(s: string | null | undefined): string | null {
  if (s == null) return null;
  const trimmed = s.trim();
  // Treat blanks and "NA"/"N/A" placeholders alike — several spec cells in the
  // source sheet use "NA" to mean "not applicable" rather than a real value.
  if (trimmed === '' || /^n\/?a$/i.test(trimmed)) return null;
  return trimmed;
}

function parseImages(s: string | null | undefined): string[] {
  if (!s) return [];
  // Dedupe: the source sheet sometimes lists the same URL twice for one
  // listing, which violates the unique (inventory_id, url) constraint on
  // inventory_images. Keep first occurrence, preserve order.
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of s.split(/[\s,]+/g)) {
    const url = p.trim();
    if (!/^https?:\/\//i.test(url) || seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }
  return out;
}

function parseDateUploaded(s: string | null | undefined): string | null {
  if (!s) return null;
  // Source "Timestamp" column, e.g. "9/4/2024, 11:37:19 AM".
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function normalizeCondition(s: string | null | undefined): 'New' | 'Used' {
  const v = (s ?? '').trim().toLowerCase();
  return v === 'new' ? 'New' : 'Used';
}

export async function parseCsv(file: File): Promise<ImportResult> {
  const text = await file.text();
  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim(),
  });

  const errors: string[] = parsed.errors.map((e) => `${e.type}: ${e.message} (row ${e.row})`);
  const rows: ImportRow[] = [];
  const usedIds = new Set<string>();

  for (const [i, raw] of parsed.data.entries()) {
    const rawId = (raw['Inventory Id'] ?? '').trim();
    const year = parseInt32(raw['Year']);
    // A parseable Year marks a real inventory row. Rows without one are sheet
    // separators (e.g. the "Summer Sale" divider) or non-vehicle goods (e.g.
    // loose tires) that belong in the Products vertical, not here.
    if (!year) {
      if (rawId) {
        errors.push(`Row ${i + 2}: no valid Year ("${rawId}") — skipped, not imported as inventory`);
      }
      continue;
    }

    const vehicle_name = (raw['Vehicle Name'] ?? '').trim() || '(untitled)';
    // The sheet has no Status column — lifecycle status lives in "Tag".
    const { status, tag } = deriveImportStatus(raw['Tag']);
    // Keep the original id when valid & unique; otherwise mint a stable one.
    const seed = `${rawId}|${vehicle_name}|${year}`;
    const { id: inventory_id, generated } = resolveInventoryId(rawId, seed, year, usedIds);
    if (generated) {
      errors.push(
        `Row ${i + 2}: inventory id "${rawId || '(blank)'}" invalid or duplicated — assigned "${inventory_id}"`,
      );
    }

    rows.push({
      status,
      inventory_id,
      year,
      regular_price: parseMoney(raw['Regular Price']),
      sale_price: parseMoney(raw['Sale Price']),
      brand: emptyToNull(raw['Brand']),
      inventory_type: emptyToNull(raw['Inventory Type']),
      tag,
      vehicle_name,
      model_number: emptyToNull(raw['Model Number']),
      mileage: parseInt32(raw['Mileage']),
      color: emptyToNull(raw['Color']),
      condition: normalizeCondition(raw['Condition']),
      youtube_link: emptyToNull(raw['Youtube Link']),
      description: (raw['Description'] ?? '').trim() || '(no description)',
      gawr: parseInt32(raw['Gawr (lbs/axle)']),
      gross_capacity: parseInt32(raw['Gross Capacity (gvwr)']),
      curb_weight: parseInt32(raw['Curb Weight (lbs)']),
      payload_capacity: parseInt32(raw['Payload Capacity (lbs)']),
      tie_downs: emptyToNull(raw['Tie Downs']),
      lights: emptyToNull(raw['Lights']),
      hitch: emptyToNull(raw['Hitch']),
      jack: emptyToNull(raw['Jack']),
      axles: emptyToNull(raw['Axles']),
      suspensions: emptyToNull(raw['Suspensions']),
      tires_wheels: emptyToNull(raw['Tires/wheels']),
      rim_size: emptyToNull(raw['Rim Size']),
      trailer_plug: emptyToNull(raw['Trailer Plug']),
      ramp: emptyToNull(raw['Ramp']),
      frame_construction: emptyToNull(raw['Frame Construction']),
      deck_construction: emptyToNull(raw['Deck Construction']),
      overall_length: emptyToNull(raw['Overall Length']),
      overall_width: emptyToNull(raw['Overall Width']),
      overall_height: emptyToNull(raw['Overall Height']),
      interior_length: emptyToNull(raw['Interior Length']),
      interior_width: emptyToNull(raw['Interior Width']),
      interior_height: emptyToNull(raw['Interior Height']),
      deck_length: emptyToNull(raw['Deck Length']),
      deck_width: emptyToNull(raw['Deck Width']),
      deck_height: emptyToNull(raw['Deck Height']),
      enclosed_door_size: emptyToNull(raw['Enclosed Door Size']),
      date_uploaded: parseDateUploaded(raw['Timestamp']),
      images: parseImages(raw['Images']),
    });
  }

  return { rows, errors };
}

export function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}
